import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, useGameState, fmtClock, pointsIfCorrect } from "@/lib/api";
import { LogoLockup, LogoMark, BrainTapLockup, BrainTapLogo } from "@/components/Logo";
import Avatar from "@/components/Avatar";
import { QRCodeSVG } from "qrcode.react";
import { Brain, Crown, Flame, Radio, Users, UtensilsCrossed, Globe2, MapPin, Trophy, Lock, Wrench, Megaphone, CalendarDays, Smartphone } from "lucide-react";
import { Watermark } from "@/components/Watermark";
import { BadgeRow } from "@/components/Badges";
import { Fireworks } from "@/components/Fireworks";

const BREAK_TOTAL = 600;

const CATEGORY_LABELS = {
  general: "General Knowledge",
  sports: "Sports Trivia",
  music: "Music Trivia",
  math: "Math & Brain Teasers",
  science: "Science Trivia",
  history: "History Trivia",
  pop_culture: "Pop Culture",
  ultimate: "Ultimate General Trivia",
};

function BreakShow({ code, venue, winner, taunt, winnerBadges, secondsLeft, intermissionCode, checkedInCount, adsOnTv, announcement }) {
  const [boards, setBoards] = useState(null);
  const [areaIdx, setAreaIdx] = useState(0);
  const rowsRef = useRef(null);
  // Gently pan the top-20 list down and back so every place gets seen on any
  // screen; pauses while a human points at it.
  useEffect(() => {
    const el = rowsRef.current;
    if (!el) return undefined;
    let raf;
    let start = performance.now();
    let pauseUntil = 0;
    const step = (now) => {
      const range = el.scrollHeight - el.clientHeight;
      if (range > 8) {
        if (now >= pauseUntil) {
          const t = ((now - start) / 9000) % 2;
          el.scrollTop = Math.round(range * (t < 1 ? t : 2 - t));
        }
      } else {
        el.scrollTop = 0;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    const pause = () => { pauseUntil = performance.now() + 5000; start = performance.now(); };
    el.addEventListener("pointerenter", pause);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("pointerenter", pause);
    };
  }, [areaIdx]);
  const [photoIdx, setPhotoIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/game/${code}/boards`);
        if (alive) setBoards(data);
      } catch (e) { /* retried on interval */ }
    };
    load();
    const iv = setInterval(load, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [code]);

  const gallery = (venue.gallery || []).map((g) => ({
    image_url: g.image_url || g,
    caption: g.caption || "",
    caption_font: g.caption_font || "baloo",
    caption_pos: g.caption_pos || "bottom",
  }));
  const specialPhotos = (venue.specials || []).filter((s) => s.image_url).map((s) => ({
    image_url: s.image_url, caption: s.title, caption_font: "baloo", caption_pos: "bottom",
  }));
  const photos = [...specialPhotos, ...gallery];

  // Home rooms: pull nearby bars' sponsored ads (matched by ZIP prefix or metro).
  const [homeAds, setHomeAds] = useState([]);
  const [adIdx, setAdIdx] = useState(0);
  useEffect(() => {
    if (venue.type !== "home") return undefined;
    let alive = true;
    api.get(`/home-ads/${venue.code}`).then(({ data }) => { if (alive) setHomeAds(data.ads || []); }).catch(() => { /* optional */ });
    return () => { alive = false; };
  }, [venue.code, venue.type]);
  useEffect(() => {
    if (homeAds.length < 2) return undefined;
    const iv = setInterval(() => setAdIdx((i) => i + 1), 10000);
    return () => clearInterval(iv);
  }, [homeAds.length]);
  useEffect(() => {
    // one impression per intermission render of each nearby ad
    if (venue.type !== "home" || !homeAds.length) return undefined;
    homeAds.forEach((ad) => { api.post(`/ads/${ad.venue_code}/impression`).catch(() => {}); });
    return undefined;
  }, [homeAds, venue.type]);

  useEffect(() => {
    const iv = setInterval(() => setAreaIdx((i) => i + 1), 20000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    if (photos.length < 2) return undefined;
    const flip = (venue.photo_flip_seconds || 5) * 1000;
    const iv = setInterval(() => setPhotoIdx((i) => i + 1), flip);
    return () => clearInterval(iv);
  }, [photos.length, venue.photo_flip_seconds]);

  const metro = (((boards && boards.metro) || venue.region || "Your area").trim()) || "Your area";
  const areas = boards
    ? [
        { label: `Tonight at ${venue.name}`, rows: (boards.venue || []).map((r) => ({ label: r.name, value: r.score !== undefined ? r.score : r.total_points, mine: false, streak: r.streak })) },
        { label: `${metro} bar league`, rows: (boards.city || []).map((r) => ({ label: r.venue_name, value: r.avg_score, mine: r.venue_code === venue.code })) },
        { label: "Nationwide scores", rows: (boards.nation || []).map((r) => ({ label: r.name, value: r.score !== undefined ? r.score : r.total_points, mine: false, streak: r.streak })), last: true },
      ]
    : [];
  if (!boards) return null;
  const area = areas.length ? areas[areaIdx % areas.length] : null;
  const photo = photos.length ? photos[photoIdx % photos.length] : null;
  const prevPhoto = photos.length > 1 ? photos[(photoIdx - 1 + photos.length) % photos.length] : null;
  const fontFor = (f) => (f === "bricolage" ? "'Bricolage Grotesque', sans-serif" : f === "mono" ? "'Space Mono', monospace" : f === "serif" ? "Georgia, 'Times New Roman', serif" : "'Baloo 2', sans-serif");

  return (
    <div className="flex w-full flex-1 flex-col" data-testid="tv-break-show">
      <div className="absolute left-1/2 top-3 z-[6] -translate-x-1/2 rounded-full border border-amber/40 bg-navy-900/85 px-6 py-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-amber-soft backdrop-blur" data-testid="tv-break-clock">
        Next game in {fmtClock(secondsLeft)}
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="flex w-[36%] flex-col overflow-hidden border-r border-ice/10 bg-navy-900/90 px-7 pt-6 pb-6" data-testid="tv-places-panel">
          {announcement?.message && (
            <div className="mb-3 flex items-center gap-2.5 rounded-2xl border border-amber/50 bg-amber/10 px-4 py-2.5" data-testid="tv-announcement-break">
              <Megaphone size={15} className="shrink-0 text-amber" />
              <span className="text-left text-xs font-bold leading-snug text-cream">{announcement.message}</span>
            </div>
          )}
          {winner && (
            <div className="mb-4 flex items-center gap-3 rounded-2xl border border-amber/40 bg-amber/10 px-4 py-3" data-testid="tv-break-winner-strip">
              <motion.img
                src="/brand/braintap-mascot.png"
                alt="BrainTap mascot"
                className="w-12 shrink-0"
                animate={{ y: [0, -8, 0], rotate: [-3, 3, -3] }}
                transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                data-testid="tv-winner-mascot"
              />
              <div className="min-w-0">
                <div className="truncate font-display font-extrabold uppercase text-amber-soft" data-testid="tv-break-winner-name">{winner.name} won last game</div>
                {taunt && <div className="truncate text-xs text-mut">"{taunt}"</div>}
              </div>
            </div>
          )}
          {(boards.theme || boards.nightly_champion) && (
            <div className="mb-3 flex flex-wrap gap-2">
              {boards.theme && (
                <div className="flex items-center gap-2 rounded-full border border-magenta/50 bg-magenta/10 px-3.5 py-1.5" data-testid="tv-theme-chip">
                  <CalendarDays size={13} className="shrink-0 text-magenta" />
                  <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-cream">
                    {boards.theme.is_today ? "Tonight" : `Next ${boards.theme.day.slice(0, 3)}`} · {boards.theme.category_name} · {boards.theme.start_time}
                  </span>
                </div>
              )}
              {boards.nightly_champion && (
                <div className="flex items-center gap-2 rounded-full border border-amber/50 bg-amber/10 px-3.5 py-1.5" data-testid="tv-nightly-champion">
                  <Crown size={13} className="shrink-0 text-amber" />
                  <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-amber-soft">
                    Last night · {(boards.nightly_champion.champion || {}).name} · {(boards.nightly_champion.champion || {}).total} pts
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div key={areaIdx} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.45 }} className="absolute inset-0">
                {area ? (
                  area.rows.length ? (
                  <div className="flex h-full flex-col">
                    <div className="mb-3 flex items-center gap-3 font-mono text-xs font-bold uppercase tracking-[0.3em] text-amber" data-testid="tv-board-label">
                      <Trophy size={14} /> {area.label}
                    </div>
                    <div ref={rowsRef} className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-2" data-testid="tv-board-rows">
                      {area.rows.slice(0, 20).map((r, i) => (
                        <div key={i} className={`flex items-center gap-3 rounded-lg border px-3.5 py-1 text-left ${r.mine ? "border-amber/60 bg-gradient-to-r from-amber/25 to-amber/5" : "border-ice/15 bg-navy-800/70"}`} data-testid={`tv-place-row-${i}`}>
                          <span className="font-mono-num w-6 text-right text-sm font-bold text-mut">{i + 1}</span>
                          {i === 0 ? <Brain size={15} className="shrink-0 text-amber" fill="#f5b335" /> : <Trophy size={13} className="shrink-0 text-mut/60" />}
                          <div className="min-w-0 flex-1 truncate font-display text-sm font-bold text-cream">
                            {r.label}
                            {r.streak >= 2 && <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-coral/15 px-1.5 py-0.5 font-mono text-[9px] font-bold text-coral"><Flame size={9} />×{r.streak}</span>}
                          </div>
                          <span className="font-mono-num text-sm font-bold text-mint">{r.value}</span>
                        </div>
                      ))}
                    </div>
                    {area.last && (
                      <div className="mt-2.5 flex shrink-0 items-center justify-center gap-2 rounded-xl border border-amber/50 bg-amber/10 px-4 py-2 text-center" data-testid="tv-look-at-phone">
                        <Smartphone size={13} className="shrink-0 text-amber" />
                        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-amber-soft">Look at your phone to see what place you got</span>
                      </div>
                    )}
                  </div>
                  ) : (
                    <div className="rounded-2xl border border-ice/15 bg-navy-800/60 px-6 py-8 text-center text-mut" data-testid="tv-board-empty">No {area.label} scores yet — the first games of the night write history.</div>
                  )
                ) : (
                  <div className="rounded-2xl border border-ice/15 bg-navy-800/60 px-6 py-8 text-center text-mut">Loading the boards…</div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          {(intermissionCode || adsOnTv) && (
            <div className="mt-3 space-y-3">
              {adsOnTv && (
                <a href="https://braintap.bar" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-3 rounded-2xl border border-grape/50 bg-navy-800/80 px-5 py-3 transition-transform hover:scale-[1.01]" data-testid="tv-play-at-home-ad">
                  <img src="/brand/braintap-mascot.png" alt="" className="h-9 w-9 shrink-0 object-contain" />
                  <span className="font-display text-base font-extrabold uppercase leading-tight text-cream">
                    Want to play at home? Go to <span className="text-amber">braintap.bar</span>
                  </span>
                </a>
              )}
              {(venue.events || []).slice(0, 3).map((ev, i) => (
                <div key={i} className="flex items-center gap-3 rounded-2xl border border-ice/15 bg-navy-800/60 px-4 py-2.5" data-testid={`tv-event-${i}`}>
                  <CalendarDays size={15} className="shrink-0 text-magenta" />
                  <span className="min-w-0 flex-1 truncate text-left text-sm font-bold text-cream">{ev.title}</span>
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] text-mut">{ev.when}</span>
                </div>
              ))}
            </div>
          )}
          {intermissionCode && (
            <div className="mt-3 flex items-center justify-center gap-4 rounded-xl border border-amber/50 bg-navy-900/90 px-4 py-2" data-testid="tv-checkin-banner">
              <span className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-amber-soft">Check-in</span>
              <span className="font-mono-num text-3xl font-extrabold tracking-[0.2em] text-amber" data-testid="tv-checkin-code">{intermissionCode}</span>
              <span className="font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-cream">{checkedInCount > 0 ? `${checkedInCount} in` : "type it on your phone"}</span>
            </div>
          )}
        </div>
        <div className="relative flex-1" data-testid="tv-photo-stage">
          {venue.type === "home" && homeAds.length ? (
            <>
              {(() => {
                const ad = homeAds[adIdx % homeAds.length];
                return ad.media_type === "video" ? (
                  <video key={ad.media_url} src={ad.media_url} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover" data-testid="tv-home-ad-video" />
                ) : (
                  <img key={ad.media_url} src={ad.media_url} alt={ad.title || "Nearby bar"} className="tk-fade-slow absolute inset-0 h-full w-full object-cover" data-testid="tv-home-ad" />
                );
              })()}
              <div className="absolute bottom-4 left-10 z-[1]" data-testid="tv-home-ad-label">
                <span className="inline-block rounded-2xl border border-magenta/50 bg-navy-900/85 px-6 py-3 text-xl font-extrabold text-cream shadow-[0_18px_60px_rgba(0,0,0,0.55)] backdrop-blur">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff9df2]">Sponsored · bar near you</span>
                  <br />
                  {homeAds[adIdx % homeAds.length].venue_name}
                  {homeAds[adIdx % homeAds.length].title && <span className="block text-sm font-bold text-amber-soft">{homeAds[adIdx % homeAds.length].title}</span>}
                </span>
              </div>
            </>
          ) : photo ? (
            <>
              {prevPhoto && <img src={prevPhoto.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
              <img key={photoIdx} src={photo.image_url} alt={photo.caption || "Venue special"} className="tk-fade-slow absolute inset-0 h-full w-full object-cover" data-testid="tv-break-photo" />
              {photo.caption && (
                <div className={`absolute left-10 right-[300px] z-[1] ${photo.caption_pos === "top" ? "top-12" : photo.caption_pos === "center" ? "top-1/2 -translate-y-1/2 text-center" : "bottom-4 text-left"}`} data-testid="tv-photo-caption-box">
                  <span
                    className="inline-block rounded-2xl border border-amber/40 bg-navy-900/85 px-8 py-4 text-4xl font-extrabold leading-tight text-cream shadow-[0_18px_60px_rgba(0,0,0,0.55)] backdrop-blur"
                    style={{ fontFamily: fontFor(photo.caption_font) }}
                    data-testid="tv-photo-caption"
                  >
                    {photo.caption}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-navy-800 to-navy-900">
              <BrainTapLockup className="float-slow w-96" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MascotTakeover({ onDone }) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center"
      initial={{ scale: 0.08, opacity: 0 }}
      animate={{ scale: [0.08, 1.65, 1.65, 0.04], opacity: [0, 1, 1, 0] }}
      transition={{ duration: 5.6, times: [0, 0.42, 0.72, 1], ease: ["backOut", "linear", "anticipate"] }}
      onAnimationComplete={onDone}
      data-testid="mascot-takeover"
    >
      <video
        src="/brand/mascot-takeover-v3.webm"
        autoPlay
        muted
        playsInline
        onEnded={onDone}
        className="w-[82vw] max-w-[880px]"
        data-testid="mascot-takeover-video"
      />
    </motion.div>
  );
}

export default function TV() {
  const { code } = useParams();
  const roomCode = code || "847291";
  const { state, secondsLeft } = useGameState(roomCode, false);
  const [takeoverDone, setTakeoverDone] = useState(false);

  useEffect(() => {
    document.title = `BrainTap — Live`;
  }, []);

  useEffect(() => {
    if (state && state.phase !== "break") setTakeoverDone(false);
  }, [state && state.phase]);

  if (!state) {
    return <div className="flex h-screen items-center justify-center bg-navy-900 font-mono text-sm uppercase tracking-[0.3em] text-mut">Warming up the big screen…</div>;
  }

  if (state.maintenance) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-navy-900 text-center" data-testid="tv-maintenance">
        <BrainTapLockup className="w-80" />
        <Wrench className="mt-4 text-amber" size={44} />
        <h1 className="font-display text-4xl font-extrabold uppercase text-cream">Quick tune-up</h1>
        <p className="max-w-md text-mut">BrainTap is in maintenance mode — games pause and come right back. See you in a minute.</p>
        {state.announcement?.message && <p className="rounded-2xl border border-amber/40 bg-amber/10 px-6 py-3 font-display text-lg font-bold text-amber-soft">{state.announcement.message}</p>}
      </div>
    );
  }

  if (state.subscription_required) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-navy-900 px-6 text-center" data-testid="tv-subscription-required">
        <BrainTapLockup className="w-80" />
        <div className="mt-4 flex items-center gap-3 rounded-full border border-amber/50 bg-amber/10 px-6 py-2.5">
          <Lock size={18} className="text-amber" />
          <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-amber-soft">Subscription required</span>
        </div>
        <h1 className="max-w-2xl font-display text-4xl font-extrabold uppercase leading-tight text-cream">
          {state.venue?.name || "This room"} needs an active subscription to run the big screen
        </h1>
        <p className="max-w-lg text-mut">
          Big Screen Mode is part of the paid plan. The show returns the moment the subscription is active again — scores, history and everything else are safe.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a href="/venue" className="neon-btn rounded-full px-10 py-4 font-display text-lg font-extrabold uppercase tracking-wide" data-testid="tv-subscribe-cta">Subscribe or renew</a>
          <a href={`mailto:braintaplive@gmail.com`} className="rounded-full border-2 border-ice/25 px-8 py-4 font-display font-extrabold uppercase tracking-wide text-mut transition-colors hover:border-amber hover:text-amber">Questions? Email us</a>
        </div>
        {state.announcement?.message && <p className="mt-2 rounded-2xl border border-grape/40 bg-grape/10 px-6 py-3 font-display text-lg font-bold text-[#dcb6ff]">{state.announcement.message}</p>}
      </div>
    );
  }

  if (state.notFound) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-navy-900 text-center" data-testid="tv-not-found">
        <LogoLockup markClass="h-12" />
        <h1 className="font-display text-4xl font-extrabold uppercase text-cream">Room not found</h1>
        <p className="text-mut">No trivia room runs on code "{roomCode}".</p>
        <Link to="/join" className="rounded-full bg-amber px-8 py-3.5 font-display font-extrabold uppercase text-navy-900">Go to join</Link>
      </div>
    );
  }

  if (state.demo_disabled) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-navy-900 text-center" data-testid="tv-demo-disabled">
        <LogoLockup markClass="h-12" />
        <h1 className="font-display text-4xl font-extrabold uppercase text-cream">Sales demo is off</h1>
        <p className="text-mut">An administrator can switch the demonstration bar back on from the admin dashboard.</p>
        <Link to="/admin" className="rounded-full bg-amber px-8 py-3.5 font-display font-extrabold uppercase text-navy-900">Admin dashboard</Link>
      </div>
    );
  }

  const { venue, phase } = state;
  const qTime = state.question_time || 10;
  const frac = Math.max(0, Math.min(1, secondsLeft / qTime));
  const winnerMoment = false;

  return (
    <div
      className="relative flex h-screen flex-col overflow-hidden text-cream"
      style={{
        background:
          "radial-gradient(circle at 20% 10%, rgba(255,180,60,0.15), transparent 50%), radial-gradient(circle at 80% 15%, rgba(196,77,240,0.20), transparent 55%), radial-gradient(circle at 50% 105%, rgba(255,77,157,0.14), transparent 55%), linear-gradient(160deg,#381a4d 0%,#261033 55%,#190a20 100%)",
      }}
      data-testid="tv-page"
    >
      <Watermark />
      {state.announcement?.message && phase !== "break" && (
        <div className="absolute left-5 top-24 z-20 flex max-w-xs items-center gap-2.5 rounded-2xl border border-amber/50 bg-navy-900/90 px-4 py-2.5 backdrop-blur" data-testid="tv-announcement">
          <Megaphone size={16} className="shrink-0 text-amber" />
          <span className="text-left text-xs font-bold leading-snug text-cream">{state.announcement.message}</span>
        </div>
      )}
      <Fireworks events={state.fireworks} durationSec={(state.fireworks_cfg && state.fireworks_cfg.duration_seconds) || 6} />
      <img src="/brand/braintap-logo-full.png" alt="braintap.bar" className="pointer-events-none absolute right-3 top-3 z-30 h-16 w-auto drop-shadow-[0_4px_18px_rgba(0,0,0,0.5)] sm:right-4 sm:h-28" data-testid="tv-braintap-logo" />
      {state.phase === "break" && !takeoverDone && <MascotTakeover onDone={() => setTakeoverDone(true)} />}
      {state.paused && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-navy-900/70 backdrop-blur-sm" data-testid="tv-paused-overlay">
          <div className="font-display text-6xl font-extrabold uppercase tracking-[0.25em] text-amber text-glow-amber">Paused</div>
        </div>
      )}

      {phase !== "break" && (
        <header className="relative z-[1] grid grid-cols-[1fr_auto_1fr] items-start px-5 pt-2 lg:px-8">
          <div className="flex flex-col items-start gap-1.5">
            <div className="flex items-center gap-2 rounded-full bg-navy-900/70 px-3.5 py-1.5 backdrop-blur">
              <span className="live-dot h-2.5 w-2.5 rounded-full bg-coral shadow-[0_0_14px_#ff5a82]" />
              <span className="font-display text-sm font-extrabold tracking-[0.2em] text-coral">LIVE</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-navy-900/60 px-3 py-0.5 font-mono text-[10px] tracking-[0.15em] text-mut backdrop-blur">
              <Users size={10} />
              <span data-testid="tv-players-online" className="text-mint">{state.players_online}</span> ONLINE
            </div>
          </div>
          <div className="flex items-center justify-center" data-testid="tv-venue-brand">
            {venue.logo_url ? (
              <img src={venue.logo_url} alt={venue.name} className="h-44 lg:h-60 max-w-[460px] lg:max-w-[680px] object-contain drop-shadow-[0_6px_22px_rgba(0,0,0,0.55)]" />
            ) : (
              <div data-testid="tv-venue-name" className="font-display text-2xl lg:text-4xl font-extrabold tracking-tight text-amber-soft drop-shadow-[0_4px_24px_rgba(245,179,53,0.4)]">{venue.name}</div>
            )}
          </div>
          <div />
        </header>
      )}

      <main className="relative z-[1] flex flex-1 flex-col items-center justify-center overflow-hidden px-8 pb-24 text-center">
        {phase === "question" && state.question && (
          <div className="flex h-full w-full flex-col items-center justify-center" data-testid="tv-question">
            <div className="mb-3 inline-block rounded-full border border-magenta/50 bg-magenta/10 px-6 py-2 font-mono text-xs font-bold uppercase tracking-[0.4em] text-[#ff9df2]">
              {(state.game_category === "Mixed Categories" && state.question && state.question.category_name) ? state.question.category_name : (CATEGORY_LABELS[state.game_category] || state.game_category)}
            </div>
            <div className="mb-5 inline-block rounded-full border border-amber/40 bg-amber/10 px-6 py-2 font-mono text-sm font-bold uppercase tracking-[0.4em] text-amber-soft">
              Question <span className="text-white">{state.q_index + 1}</span> / {state.question_total}
            </div>
            <h1 className={`mx-auto mb-6 max-w-[86%] font-display font-extrabold leading-[1.2] ${(state.question.text || "").length > 150 ? "text-[3vh]" : (state.question.text || "").length > 100 ? "text-[3.6vh]" : "text-[4.6vh]"}`} data-testid="tv-question-text" style={{ textShadow: "0 0 34px rgba(255,43,253,0.3), 0 4px 24px rgba(40,0,90,0.7)", textWrap: "balance" }}>
              {state.question.text}
            </h1>
            <div className={`mx-auto grid max-w-[76vw] flex-none grid-cols-1 gap-3 sm:grid-cols-2 sm:max-w-[76vw]`}>
              {state.question.options.map((opt, i) => (
                <div key={i} data-testid={`tv-answer-${i}`} className={`card-navy flex items-center gap-3 overflow-hidden rounded-2xl text-left font-display font-bold ${(state.question.text || "").length > 100 ? "p-3.5 text-lg" : "p-4 text-xl"}`} style={{ boxShadow: "0 4px 0 rgba(0,0,0,0.25), 0 6px 20px rgba(0,0,0,0.3)" }}>
                  <span className={`font-mono-num flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#c44df0] to-[#8b2fd6] font-bold ${(state.question.text || "").length > 100 ? "h-8 w-8 text-sm" : "h-10 w-10 text-lg"}`}>{i + 1}</span>
                  {opt}
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-none flex-col items-center">
              <div className="h-3 w-[38vw] overflow-hidden rounded-full border border-ice/25 bg-navy-900/90">
                <div className="h-full" style={{ width: `${frac * 100}%`, background: "linear-gradient(90deg,#ff3d71,#ffb020,#ffd68a)", boxShadow: "0 0 12px rgba(255,180,60,0.6)", transition: "width 0.25s linear" }} />
              </div>
              <div className={`font-mono-num mt-2 text-3xl font-bold ${secondsLeft <= 3 ? "text-coral pulse-urgent" : "text-volt text-glow-volt"}`} data-testid="tv-timer-num">{Math.ceil(secondsLeft)}</div>
              <div className="mt-2 flex items-center gap-3 rounded-full border border-amber/40 bg-navy-900/85 px-5 py-1.5">
                <span className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-mut">Points if correct now</span>
                <span className="font-mono-num text-xl font-bold text-mint" data-testid="tv-points-value">{pointsIfCorrect(secondsLeft, qTime, state.speed_min, state.speed_max)}</span>
              </div>
            </div>
          </div>
        )}

        {phase === "reveal" && state.question && (
          <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden" data-testid="tv-reveal">
            <div className={`answer-pop mx-auto mb-5 font-display font-extrabold leading-none ${(state.question.text || "").length > 100 || (state.question.options[state.question.correct] || "").length > 26 ? "text-[6vh]" : "text-[8.5vh]"}`} data-testid="tv-answer-pop"
              style={{ backgroundImage: "linear-gradient(135deg,#34d8a8,#7ee89a)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", animation: "tk-answer-pop 0.9s cubic-bezier(0.34,1.56,0.64,1) both" }}
            >
              {state.question.options[state.question.correct]}
            </div>
            <style>{`@keyframes tk-answer-pop { 0% { transform: scale(0.3) rotate(-8deg); opacity: 0; filter: blur(8px);} 55% { transform: scale(1.15) rotate(2deg); opacity: 1; filter: blur(0);} 100% { transform: scale(1) rotate(0); opacity: 1;} }`}</style>
            <div className="mx-auto mb-4 grid max-w-[76vw] flex-none grid-cols-2 gap-3">
              {state.question.options.map((opt, i) => (
                <div key={i} className={`flex items-center gap-3 rounded-2xl border-2 p-3.5 text-left font-display text-lg font-bold transition-all ${
                  i === state.question.correct ? "border-mint bg-gradient-to-br from-[#1a8e6a] to-[#0d4a37] text-white" : "border-ice/10 bg-navy-800/40 text-mut opacity-20"
                }`} data-testid={`tv-reveal-answer-${i}`}>
                  <span className={`font-mono-num flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base font-bold ${i === state.question.correct ? "bg-mint text-[#001a12]" : "bg-navy-700"}`}>{i + 1}</span>
                  {opt}
                </div>
              ))}
            </div>
            {state.question.fact && (
              <div className="mx-auto max-w-2xl flex-none rounded-2xl border-l-4 border-amber-soft bg-navy-800/90 p-4 text-left" data-testid="tv-fact">
                <div className="mb-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-amber-soft">Did you know</div>
                <p className="text-base leading-relaxed text-ice">{state.question.fact}</p>
              </div>
            )}
            <div className="mt-4 inline-block flex-none rounded-full border border-ice/25 bg-navy-900/85 px-6 py-2 font-mono text-xs font-bold uppercase tracking-[0.35em] text-[#c9a3ff]">
              Next question in {Math.ceil(secondsLeft)}
            </div>
          </div>
        )}

        {phase === "leaderboard" && (
          <div className="w-full" data-testid="tv-leaderboard">
            <h1 className="mb-2 font-display text-4xl font-extrabold uppercase tracking-[0.1em] text-amber text-glow-amber">Final standings</h1>
            {state.winner && (
              <div className="mb-6 flex items-center justify-center gap-4">
                <Avatar icon={state.winner.avatar_icon} color={state.winner.avatar_color} size={52} />
                <div className="text-left">
                  <div className="font-display text-2xl font-extrabold text-cream">{state.winner.name} takes the glory</div>
                  <div className="font-mono text-sm text-amber-soft">"{state.taunt}"</div>
                </div>
              </div>
            )}
            <div className="mx-auto max-w-3xl space-y-2">
              {state.standings.map((p, i) => (
                <div key={p.player_id} className={`grid grid-cols-[3.5rem_3rem_1fr_auto] items-center gap-4 rounded-2xl border px-5 py-3 text-xl ${i === 0 ? "medal-gold" : i === 1 ? "medal-silver" : i === 2 ? "medal-bronze" : "border-ice/20 bg-navy-800/70"}`} data-testid={`tv-lb-row-${i}`}>
                  <span className={`font-mono-num font-bold ${i === 0 ? "medal-num-gold" : i === 1 ? "medal-num-silver" : i === 2 ? "medal-num-bronze" : "text-mut"}`}>{i + 1}</span>
                  <Avatar icon={p.avatar_icon} color={p.avatar_color} config={p.avatar_config} size={38} />
                  <span className="flex items-center gap-2 text-left font-display font-bold">
                    {p.name}
                    {p.streak >= 2 && (
                      <span className="flex items-center gap-1 rounded-full bg-coral/15 px-2 py-0.5 font-mono text-[10px] font-bold text-coral" data-testid={`tv-lb-streak-${i}`}>
                        <Flame size={11} />×{p.streak}
                      </span>
                    )}
                  </span>
                  <span className="font-mono-num font-bold text-mint">{p.score}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.35em] text-[#c9a3ff]">10-minute break starting…</p>
          </div>
        )}

        {phase === "break" && (
          <div className="relative -mx-8 -mb-24 flex min-h-0 w-[calc(100%+4rem)] flex-1 flex-col self-stretch" data-testid="tv-break">
            <BreakShow code={roomCode} venue={venue} winner={state.winner} taunt={state.taunt} winnerBadges={state.winner_badges} secondsLeft={secondsLeft} intermissionCode={state.intermission_code} checkedInCount={state.checked_in_count || 0} adsOnTv={state.ads_on_tv !== false} announcement={state.announcement} />
          </div>
        )}
      </main>

      {phase !== "break" && venue.specials && venue.specials.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 z-[5] overflow-hidden border-t-2 border-ice/15 bg-navy-900/90 py-3.5 backdrop-blur" data-testid="tv-specials-marquee">
          <div className="marquee-track items-center" style={{ animationDuration: `${venue.ticker_speed || 42}s` }}>
            {[0, 1].map((half) => (
              <div key={half} className="flex items-center gap-10 pr-10">
                <span className="flex items-center gap-3 whitespace-nowrap font-mono text-sm font-bold uppercase tracking-[0.3em] text-amber">
                  <UtensilsCrossed size={16} /> Tonight's specials
                  <span className="inline-block h-2.5 w-2.5 rotate-45 bg-amber/70" />
                </span>
                {venue.specials.map((s, i) => (
                  <span key={i} className="flex items-center gap-4 whitespace-nowrap">
                    {s.image_url && <img src={s.image_url} alt="" className="h-12 w-20 rounded-lg object-cover ring-1 ring-ice/25" />}
                    <span className="font-display text-2xl font-extrabold text-cream">{s.title}</span>
                    {s.description && <span className="text-base text-mut">{s.description}</span>}
                    <span className="inline-block h-2.5 w-2.5 rotate-45 bg-amber/70" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`glass fixed z-10 hidden flex-col items-center gap-2 rounded-l-2xl border-2 border-r-0 border-amber/50 p-3 sm:flex ${phase === "break" ? "bottom-4 right-0" : "bottom-24 right-0"}`} data-testid="tv-join-card">
        <div className="text-center font-display text-base font-extrabold uppercase leading-tight text-cream" data-testid="tv-join-caption">
          Scan to join
          <br />
          <span className="text-amber-soft">on your phone</span>
        </div>
        <div className="rounded-lg bg-white p-1.5">
          <QRCodeSVG value={`${window.location.origin}/join?code=${roomCode}`} size={84} fgColor="#190a20" />
        </div>
      </div>
    </div>
  );
}
